import { useState, useEffect } from 'react';
import { Box, Container, Stack, Typography, useTheme } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';

import useTable from 'hooks/useTable';
import MuiDataGrid from 'components/MuiDataGrid';
import { FormProps, IMedication } from '../PatientList/api/type';
import ListActionButtons from './ListActionButtons';
import MedicationDialog from './MedicationDialog';
import { formatStringToDate } from 'utils/formatting';
import useNotification from 'hooks/useNotification';
import { updateMedicationHidden } from 'apis/admin';

interface Props extends FormProps {
  defaultList?: IMedication[];
}

const MedicationList = (props: Props) => {
  const { setValue, defaultList } = props;
  const { palette } = useTheme();
  const { onSuccess, onFail } = useNotification();
  const { list, setList } = useTable<IMedication>();
  const [defaultValue, setDefaultValue] = useState<Partial<IMedication> | null>(
    null
  );

  useEffect(() => setList(defaultList ?? []), [defaultList, setList]);
  useEffect(() => {
    setValue(
      'medication',
      // Store object in API update format
      list.map(item => ({
        ...item,
        prescriptionTime: formatStringToDate(item.prescriptionTime),
        medicationTime: formatStringToDate(item.medicationTime),
      }))
    );
  }, [list, setValue]);

  const onCreateList = (data: IMedication) => setList(list.concat(data));
  const onUpdateList = (data: IMedication) =>
    setList(
      list.map(item =>
        item.ptMedicationNo === data.ptMedicationNo ? data : item
      )
    );

  const ActionButtons = ({ row }: { row: IMedication }) => {
    const onUpdate = () => setDefaultValue(row);

    const onToggleHidden = async () => {
      const nextHidden = !row.isHidden; // 클릭 후 상태(목표)

      try {
        await updateMedicationHidden(row.ptMedicationUuid);

        setList(prev =>
          prev.map(item =>
            item.ptMedicationNo === row.ptMedicationNo
              ? { ...item, isHidden: nextHidden }
              : item
          )
        );

        onSuccess(
          nextHidden ? 'Item has been hidden.' : 'Item is now visible.'
        );
      } catch (error) {
        console.error('Error occurred while toggling visibility:', error);
        onFail(
          nextHidden
            ? 'Failed to hide the item.'
            : 'Failed to make the item visible.',
          error
        );
      }
    };

    const onDelete = () =>
      setList(prev =>
        prev.filter(item => item.ptMedicationNo !== row.ptMedicationNo)
      );

    return (
      <ListActionButtons
        onUpdate={onUpdate}
        onToggleHidden={onToggleHidden}
        isHidden={row.isHidden}
        onDelete={onDelete}
      />
    );
  };

  const columns: GridColDef[] = [
    { field: 'ptMedicationNo', headerName: 'Medication No.' },
    {
      field: 'prescriptionTime',
      headerName: 'Prescription Time',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    {
      field: 'medicationTime',
      headerName: 'Administration Time',
      valueFormatter: ({ value }) => formatStringToDate(value, 'hh:mm a'),
    },
    { field: 'medicationId', headerName: 'Ingredient Code' },
    { field: 'medicationName', headerName: 'Generic Name' },
    {
      align: 'right',
      field: 'actions',
      headerName: '',
      renderCell: ({ row }) => <ActionButtons row={row} />,
    },
  ];

  return (
    <Container
      maxWidth={false}
      disableGutters
      component="section"
      sx={{ background: '#fff', p: 2 }}
    >
      <Stack spacing={2}>
        <Box display="flex" alignItems="center">
          <Typography variant="h5" sx={{ color: `${palette.primary.main}` }}>
            Medication Records (Editable)
          </Typography>
          <Typography
            sx={{
              fontSize: '14px',
              fontWeight: 400,
              color: '#555555a4',
              marginLeft: '30px',
            }}
          >
            (For additions, please contact SmartNurse ENR.)
          </Typography>
          {/* <Button
            size="small"
            variant="outlined"
            onClick={onCreateStart}
            sx={{ ml: 3 }}
          >
            Add
          </Button> */}
        </Box>

        <MuiDataGrid
          columns={columns}
          rows={list.map(item => ({ ...item, id: item.ptMedicationNo }))}
        />
      </Stack>

      {defaultValue && (
        <MedicationDialog
          isUpdate={!!defaultValue.ptMedicationNo}
          defaultValue={defaultValue}
          onClose={() => setDefaultValue(null)}
          onSearchNo={(no: number) =>
            list.find(item => item.ptMedicationNo === no) ? true : false
          }
          onSave={defaultValue.ptMedicationNo ? onUpdateList : onCreateList}
        />
      )}
    </Container>
  );
};

export default MedicationList;
